import { Compass, Sparkles, Workflow, Zap } from "lucide-react";

export default function HowItWorks() {
  const steps = [
    {
      icon: Compass,
      title: "Discovery & Strategy Audit",
      description:
        "We analyze your workflows, identify high-impact AI opportunities, and map a transformation roadmap aligned with your business goals.",
      timeline: "Week 1-2",
      deliverable: "Strategy report + transformation roadmap",
    },
    {
      icon: Sparkles,
      title: "AI Enablement & Training",
      description:
        "We empower your team with the knowledge and confidence to leverage AI effectively, ensuring smooth adoption across your organization.",
      timeline: "Week 2-3",
      deliverable: "Team training + adoption framework",
    },
    {
      icon: Workflow,
      title: "System Development & Integration",
      description:
        "We design and deploy intelligent systems tailored to your workflows. Seamless integration with existing tools. Zero operational disruption.",
      timeline: "Week 3-6",
      deliverable: "Live AI systems + documentation",
    },
    {
      icon: Zap,
      title: "Continuous Optimization",
      description:
        "Real-time monitoring, performance optimization, and strategic refinement. Your systems evolve and improve continuously.",
      timeline: "Ongoing",
      deliverable: "Monthly insights + system enhancements",
    },
  ];

  return (
    <section id="how" className="py-20 md:py-32 px-6 bg-deep-navy">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-1 rounded-full border border-bronze/30 bg-bronze/5 mb-6 text-xs text-bronze font-bold uppercase tracking-wider">
            Our Process
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
            Your Transformation Journey
          </h2>
          <p className="text-lg md:text-xl text-light-gray/80 max-w-3xl mx-auto">
            From strategic clarity to intelligent systems — a proven framework for sustainable AI adoption.
          </p>
        </div>

        {/* Steps Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-8">
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <div
                key={index}
                className="bg-dark-card rounded-xl border border-bronze/10 p-8 hover:border-bronze/30 hover:-translate-y-2 transition-all duration-300 shadow-lg"
              >
                {/* Icon */}
                <div className="w-16 h-16 rounded-lg bg-bronze/10 flex items-center justify-center mb-6">
                  <Icon className="w-8 h-8 text-bronze" />
                </div>

                {/* Content */}
                <h3 className="text-xl font-bold text-foreground mb-3">
                  {index + 1}. {step.title}
                </h3>
                <p className="text-base text-light-gray/80 mb-6 leading-relaxed">
                  {step.description}
                </p>

                {/* Timeline & Deliverable */}
                <div className="pt-4 border-t border-bronze/10">
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-medium-gray">Timeline:</span>
                    <span className="text-bronze font-semibold">{step.timeline}</span>
                  </div>
                  <div className="text-sm">
                    <span className="text-medium-gray">Deliverable: </span>
                    <span className="text-light-gray">{step.deliverable}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
